import { STEP_UP_TTL_SEC, VAULT_SECOND_FACTOR_MAX_AGE_SEC } from '@fos/shared/contracts/identity';
import { Mail } from 'lucide-react';
import { type FormEvent, useEffect, useState, useSyncExternalStore } from 'react';
import { StepUpCodeFields, useStepUpCode } from '../features/auth/components/StepUpCode';
import { errorMessage, isApiError } from '../lib/api';
import { confirmStepUp } from '../lib/auth';
import { stepUpStore } from '../lib/step-up';
import { FormMessage } from '../ui/Blocks';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Field } from '../ui/Field';
import { PasswordInput } from '../ui/Input';

/**
 * Okno potwierdzenia tożsamości (step-up, docs/API_AUTH.md). Otwierane globalnie przez lib/step-up (link tRPC
 * i withStepUp); po potwierdzeniu akcja jest ponawiana automatycznie. Rodzaj z `error.details.kind`:
 * „Potwierdź hasłem” (10 min) albo „Potwierdź kodem z maila” (Fala 7: „Wyślij kod” → kod; kod zastępuje też hasło).
 */
export function StepUpDialog() {
  const open = useSyncExternalStore(stepUpStore.subscribe, stepUpStore.isOpen, stepUpStore.isOpen);
  const kind = useSyncExternalStore(stepUpStore.subscribe, stepUpStore.kind, stepUpStore.kind);
  return kind === 'second_factor' ? <CodeStepUp open={open} /> : <PasswordStepUp open={open} />;
}

function PasswordStepUp({ open }: { open: boolean }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword('');
      setError(null);
      setPending(false);
    }
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!password) {
      setError('Podaj hasło');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await confirmStepUp(password);
      setPassword('');
      stepUpStore.settle(true);
    } catch (err) {
      setError(
        isApiError(err) && err.code === 'UNAUTHENTICATED' && err.status === 401
          ? 'Nieprawidłowe hasło'
          : errorMessage(err),
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) stepUpStore.settle(false);
      }}
      width="s"
      title="Potwierdź hasłem"
      description={`Ta zmiana wymaga ponownego potwierdzenia tożsamości. Potwierdzenie jest ważne ${Math.round(STEP_UP_TTL_SEC / 60)} minut.`}
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label="Hasło" error={error && error !== 'Podaj hasło' ? null : error}>
          <PasswordInput
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error && error !== 'Podaj hasło' && <FormMessage>{error}</FormMessage>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => stepUpStore.settle(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" loading={pending}>
            Potwierdź
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CodeStepUp({ open }: { open: boolean }) {
  const state = useStepUpCode();
  const { reset } = state;

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const confirm = async (code?: string) => {
    if (await state.verify(code)) stepUpStore.settle(true);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!state.sent) void state.send();
    else void confirm();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) stepUpStore.settle(false);
      }}
      width="s"
      title="Potwierdź kodem z maila"
      description={`Ta akcja wymaga drugiego składnika. Potwierdzenie kodem jest ważne ${Math.round(VAULT_SECOND_FACTOR_MAX_AGE_SEC / 60)} minut.`}
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {state.sent ? (
          <StepUpCodeFields state={state} onComplete={(code) => void confirm(code)} />
        ) : (
          <div className="flex items-start gap-2.5 rounded-lg bg-surface px-3 py-2.5">
            <Mail size={15} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
            <p className="text-caption text-fg-secondary">Wyślemy 6-cyfrowy kod na adres e-mail Twojego konta.</p>
          </div>
        )}
        {!state.sent && state.error && <FormMessage>{state.error}</FormMessage>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => stepUpStore.settle(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" loading={state.pending}>
            {state.sent ? 'Potwierdź' : 'Wyślij kod'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
